// MARK://dhk8/pos/features/billing/global-day-page
// Factura global del día (ventas al público en general). Vista previa de los
// totales del día seleccionado + generación del CFDI global. Ported from
// sc-restaurant's GlobalDayPage.
//
// Wired to clients/invoices.previewGlobalDay / generateGlobalDay (stub-mode in
// dev — TODO backend dhk8-erp-invoices /v1/invoices/global-day*).

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarDays } from 'lucide-react'

import { generateGlobalDay, previewGlobalDay, type GlobalDayPreviewDto, type InvoiceDto } from '../../lib/clients/invoices.js'
import { formatCents } from '../../lib/money.js'
import { billingStyles } from './billing-styles.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

function yesterdayYmd(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

export default function GlobalDayPage() {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [date, setDate] = useState(yesterdayYmd())
  const [preview, setPreview] = useState<GlobalDayPreviewDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generated, setGenerated] = useState<InvoiceDto | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    setGenerated(null)
    previewGlobalDay(date, ctrl.signal)
      .then((p) => setPreview(p))
      .catch((e) => {
        setPreview(null)
        setError(e instanceof Error ? e.message : 'No se pudo obtener la vista previa')
      })
      .finally(() => setLoading(false))
    return () => ctrl.abort()
  }, [date])

  async function generate() {
    if (!preview || preview.ordersCount === 0) return
    if (!(await confirm({
      title: 'Generar factura global',
      message: `Agrupa ${preview.ordersCount} órdenes del público en general del ${date}.`,
      confirmLabel: 'Generar',
    }))) return
    setBusy(true)
    setError(null)
    try {
      const inv = await generateGlobalDay(date)
      setGenerated(inv)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo generar la factura global')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dhk8-bil">
      <header className="dhk8-bil__head">
        <h1 className="dhk8-bil__title">
          <CalendarDays size={18} className="dhk8-bil__title-ico" aria-hidden />
          Factura global del día
        </h1>
        <p className="dhk8-bil__sub">Agrupa en un solo CFDI todas las ventas del día al público en general (sin factura nominativa). Se timbra con uso S01 y receptor genérico.</p>
      </header>

      <div className="dhk8-bil__filters">
        <label>Fecha<input type="date" value={date} max={yesterdayYmd()} onChange={(e) => setDate(e.target.value)} /></label>
      </div>

      {error && <div className="dhk8-bil__error">{error}</div>}
      {loading && <div className="dhk8-bil__notice">Calculando totales del día…</div>}

      {preview && !loading && (
        <section className="dhk8-bil__card">
          <h2>Vista previa — {preview.date}</h2>
          {preview.ordersCount === 0 ? (
            <p className="dhk8-bil__notice" style={{ margin: 0 }}>No hay órdenes facturables al público en general en esa fecha.</p>
          ) : (
            <>
              <table className="dhk8-bil__kv">
                <tbody>
                  <tr><td>Órdenes incluidas</td><td>{preview.ordersCount}</td></tr>
                  <tr><td>Subtotal</td><td className="dhk8-bil__money">{formatCents(preview.subtotalCents)}</td></tr>
                  <tr><td>Descuento</td><td className="dhk8-bil__money">{formatCents(preview.discountCents)}</td></tr>
                  <tr><td>IVA</td><td className="dhk8-bil__money">{formatCents(preview.taxCents)}</td></tr>
                  <tr><td><strong>Total</strong></td><td className="dhk8-bil__money"><strong>{formatCents(preview.totalCents)}</strong></td></tr>
                </tbody>
              </table>
              <div className="dhk8-bil__actions">
                <button className="dhk8-bil__btn dhk8-bil__btn--primary" disabled={busy || !!generated} onClick={generate}>{busy ? 'Generando…' : 'Generar factura global'}</button>
              </div>
            </>
          )}
        </section>
      )}

      {generated && (
        <section className="dhk8-bil__card">
          <h2>Factura global generada</h2>
          <p className="dhk8-bil__notice" style={{ margin: 0 }}>
            {generated.serie}-{generated.folio} · {formatCents(generated.totalCents)} · estado {generated.status}
          </p>
          <div className="dhk8-bil__actions">
            <button className="dhk8-bil__btn" onClick={() => navigate(`/billing/${generated.id}`)}>Ver factura</button>
          </div>
        </section>
      )}

      <style>{billingStyles}</style>
    </div>
  )
}
